import { action, computed, observable } from 'mobx'
import { type IValidationSchema, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  addGenericErrorForProperty,
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import type { EntityDescriptor } from '@/features/legends/editor/entities'
import type { Routing } from '@/features/legends/routing/routing'
import { FiltersBuilderViewModel } from '@/features/legends/filters/FiltersBuilderViewModel'
import { FilterScopes } from '@/features/legends/filters/filter-scope'
import { validateObject } from '@/utils/validx-util'
import type { RoutingPlanEditorViewModel } from './RoutingPlanEditorViewModel'
import { RoutingPlansBuilderViewModel } from './RoutingPlansBuilderViewModel'

/**
 * The legend's routing: qualifying requirements every candidate must meet,
 * and plans that narrow the pool for a while.
 */
export class RoutingEditorViewModel extends LegendPartViewModelBase {
  private readonly _schema: IValidationSchema<RoutingEditorViewModel> = {
    requirements: [validateObject('There are invalid filters')],
    plans: [validateObject('There are invalid routing plans')],
  }

  /**
   * The requirements every candidate must meet.
   */
  readonly requirements: FiltersBuilderViewModel

  /**
   * The plans that narrow the candidates for a while, in order.
   */
  readonly plans: RoutingPlansBuilderViewModel

  /**
   * What the inspector edits: the requirements or one of the plans. Starts
   * on the requirements.
   */
  @observable.ref accessor selectedEditor:
    FiltersBuilderViewModel | RoutingPlanEditorViewModel | null

  readonly validation = validationContext(this, this._schema)

  constructor(dependencies: LegendPartDependencies) {
    super(dependencies)
    this.requirements = new FiltersBuilderViewModel({
      dependencies,
      scope: FilterScopes.ALL,
      context: this,
    })
    this.plans = new RoutingPlansBuilderViewModel(dependencies, this)
    this.selectedEditor = this.requirements
    this.lifecycle.manage(this.requirements)
    this.lifecycle.manage(this.plans)
  }

  /**
   * Routing plans are entities, so their IDs must be unique.
   */
  @computed
  get entityDescriptors(): EntityDescriptor[] {
    return this.plans.plans.map((planVm) => ({
      type: 'RoutingPlan',
      entity: planVm,
    }))
  }

  /**
   * Whether the inspector edits the requirements.
   */
  @computed
  get isEditingRequirements() {
    return this.selectedEditor === this.requirements
  }

  @computed
  private get _json(): Routing {
    return {
      requirements: this.requirements.toJSON(),
      plans: this.plans.toJSON(),
    }
  }

  /**
   * Sets the routing from the legend document.
   */
  @action
  hydrate(routing: Routing | undefined) {
    this.requirements.hydrate(routing?.requirements)
    this.plans.hydrate(routing?.plans)
  }

  /**
   * Opens the requirements or a plan in the inspector, or closes it.
   */
  @action.bound
  edit(editor: FiltersBuilderViewModel | RoutingPlanEditorViewModel | null) {
    this.selectedEditor = editor
  }

  /**
   * Opens the requirements in the inspector.
   */
  @action.bound
  editRequirements() {
    this.edit(this.requirements)
  }

  /**
   * Shows the analyzer's errors on the requirements and plans.
   */
  receiveErrors(descriptor: ErrorDescriptor | null | undefined) {
    addGenericErrorForProperty(
      this,
      descriptor?.inner?.requirements,
      'requirements',
      'There are invalid filters',
    )
    addGenericErrorForProperty(
      this,
      descriptor?.inner?.plans,
      'plans',
      'There are invalid routing plans',
    )
    receiveErrors(this, descriptor?.inner)
  }

  /**
   * The routing as the legend document stores it.
   */
  toJSON(): Routing {
    return this._json
  }
}
